import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Plus, Pencil, Trash2, Loader2, Database,
  AlertTriangle, CheckCircle, Zap, RefreshCw
} from "lucide-react";
import { FoodFormDialog } from "@/components/food-admin/FoodFormDialog";
import { UsdaSearchDialog } from "@/components/food-admin/UsdaSearchDialog";

const CATEGORIES = [
  { value: "fruits", label: "Плодове" },
  { value: "vegetables", label: "Зеленчуци" },
  { value: "legumes", label: "Бобови / Зърнени" },
  { value: "grains", label: "Зърна / Хляб" },
  { value: "dairy", label: "Млечни" },
  { value: "meat_fish", label: "Месо / Риба" },
  { value: "other", label: "Други" },
];

const EMPTY_FORM = {
  food: "", food_en: "", category: "other", dil_100g: "", calories_100g: "",
  protein_100g: "", carbs_100g: "", fat_100g: "", fiber_100g: "",
  sat_fat_100g: "", omega_6_3_ratio: "", dii_percent: "", fodmap: "low",
};

export default function FoodDatabaseManager({ API, isDark }) {
  const [foods, setFoods] = useState([]);
  const [total, setTotal] = useState(0);
  const [incomplete, setIncomplete] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [editFood, setEditFood] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [showUsda, setShowUsda] = useState(false);
  const [autofilling, setAutofilling] = useState(null);
  const [batchFilling, setBatchFilling] = useState(false);

  const fetchFoods = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/foods`);
      setFoods(data.foods || []);
      setTotal(data.total || 0);
      setIncomplete(data.incomplete || 0);
    } catch { toast.error("Грешка при зареждане"); }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchFoods(); }, [fetchFoods]);

  const filtered = foods.filter(f => {
    if (searchTerm && !f.food.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterCat !== "all" && f.category !== filterCat) return false;
    if (filterStatus === "complete" && f.dil_100g == null) return false;
    if (filterStatus === "incomplete" && f.dil_100g != null) return false;
    return true;
  });

  const openEdit = (f) => {
    setForm({
      food: f.food || "", category: f.category || "other",
      dil_100g: f.dil_100g ?? "", calories_100g: f.calories_100g ?? "",
      protein_100g: f.protein_100g ?? "", carbs_100g: f.carbs_100g ?? "",
      fat_100g: f.fat_100g ?? "", fiber_100g: f.fiber_100g ?? "",
      sat_fat_100g: f.sat_fat_100g ?? "", omega_6_3_ratio: f.omega_6_3_ratio ?? "",
      dii_percent: f.dii_percent ?? "",
    });
    setEditFood(f);
    setShowForm(true);
  };

  const openNew = () => { setForm(EMPTY_FORM); setEditFood(null); setShowForm(true); };

  const saveFood = async () => {
    if (!form.food.trim()) { toast.error("Въведете име"); return; }
    if (!editFood) {
      const existingNames = foods.map(f => f.food.toLowerCase().trim());
      if (existingNames.includes(form.food.toLowerCase().trim())) {
        toast.error(`Храната "${form.food}" вече съществува в базата данни`);
        return;
      }
    }
    const payload = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === "" || v === null) continue;
      payload[k] = ["food", "category"].includes(k) ? v : parseFloat(v);
    }
    try {
      if (editFood) {
        await axios.put(`${API}/admin/foods/${editFood.id}`, payload);
        toast.success("Храната е обновена");
      } else {
        await axios.post(`${API}/admin/foods`, payload);
        toast.success("Храната е добавена");
      }
      setShowForm(false);
      fetchFoods();
    } catch (e) { toast.error(e.response?.data?.detail || "Грешка"); }
  };

  const deleteFood = async (id) => {
    if (!window.confirm("Изтриване на тази храна?")) return;
    try {
      await axios.delete(`${API}/admin/foods/${id}`);
      toast.success("Изтрита");
      fetchFoods();
    } catch { toast.error("Грешка"); }
  };

  const addFromUsda = (r) => {
    setForm({
      food: r.description, category: "other",
      dil_100g: r.dil_100g?.toString() ?? "", calories_100g: r.calories_100g?.toString() ?? "",
      protein_100g: r.protein_100g?.toString() ?? "", carbs_100g: r.carbs_100g?.toString() ?? "",
      fat_100g: r.fat_100g?.toString() ?? "", fiber_100g: r.fiber_100g?.toString() ?? "",
      sat_fat_100g: r.sat_fat_100g?.toString() ?? "", omega_6_3_ratio: "",
      dii_percent: r.dii_percent?.toString() ?? "",
    });
    setShowUsda(false);
    setEditFood(null);
    setShowForm(true);
  };

  const autofillFood = async (food) => {
    setAutofilling(food.id);
    try {
      await axios.post(`${API}/admin/foods/${food.id}/usda-autofill`, { query: food.food });
      toast.success(`${food.food} — попълнена от USDA`);
      fetchFoods();
    } catch (e) { toast.error(e.response?.data?.detail || "Няма USDA резултат"); }
    finally { setAutofilling(null); }
  };

  const batchAutofill = async () => {
    setBatchFilling(true);
    try {
      const { data } = await axios.post(`${API}/admin/foods/batch-autofill`);
      toast.success(`Попълнени: ${data.filled}, Грешки: ${data.errors.length}`);
      if (data.errors.length > 0) toast.warning(`${data.errors.length} грешки при autofill`);
      fetchFoods();
    } catch { toast.error("Грешка при batch autofill"); }
    finally { setBatchFilling(false); }
  };

  return (
    <div className="space-y-4" data-testid="food-db-manager">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Общо храни", value: total, icon: Database, color: "#0EA5E9" },
          { label: "Попълнени", value: total - incomplete, icon: CheckCircle, color: "#10B981" },
          { label: "Непопълнени", value: incomplete, icon: AlertTriangle, color: "#F59E0B" },
        ].map(s => (
          <Card key={s.label} className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</p>
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              </div>
              <s.icon className="w-8 h-8" style={{ color: `${s.color}30` }} />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Търсене на храна..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-[#F8FAFC]'}`}
                data-testid="food-search-input" />
            </div>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'}`}
              data-testid="food-filter-category">
              <option value="all">Всички категории</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg text-sm border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200'}`}
              data-testid="food-filter-status">
              <option value="all">Всички</option>
              <option value="complete">Попълнени</option>
              <option value="incomplete">Непопълнени</option>
            </select>
            <Button size="sm" variant="outline" onClick={() => setShowUsda(true)} data-testid="usda-search-btn">
              <Zap className="w-4 h-4 mr-1" /> USDA Търсене
            </Button>
            {incomplete > 0 && (
              <Button size="sm" variant="outline" onClick={batchAutofill} disabled={batchFilling} data-testid="batch-autofill-btn">
                {batchFilling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Auto-fill всички ({incomplete})
              </Button>
            )}
            <Button size="sm" className="gradient-primary text-white" onClick={openNew} data-testid="add-food-btn">
              <Plus className="w-4 h-4 mr-1" /> Добави храна
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Food Table */}
      <Card className={isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <table className="w-full text-sm" data-testid="food-table">
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <tr className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <th className="text-left px-4 py-3 font-medium">Храна</th>
                  <th className="text-left px-2 py-3 font-medium">Кат.</th>
                  <th className="text-right px-2 py-3 font-medium">DIL/100g</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">20g</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">30g</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">40g</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">50g</th>
                  <th className="text-right px-2 py-3 font-medium">DII%</th>
                  <th className="text-right px-2 py-3 font-medium hidden lg:table-cell">kcal</th>
                  <th className="text-right px-2 py-3 font-medium hidden lg:table-cell">Фибри</th>
                  <th className="text-right px-2 py-3 font-medium hidden lg:table-cell">Нас.м.</th>
                  <th className="px-2 py-3 font-medium hidden lg:table-cell">FODMAP</th>
                  <th className="px-3 py-3 text-right font-medium">Действия</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {filtered.map(f => {
                  const isInc = f.dil_100g == null;
                  return (
                    <tr key={f.id} className={`${isInc ? (isDark ? 'bg-amber-900/10' : 'bg-amber-50/50') : ''} hover:${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`} data-testid={`food-row-${f.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {isInc && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                          <span className={`font-medium truncate max-w-[200px] ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>{f.food}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{CATEGORIES.find(c => c.value === f.category)?.label || f.category}</span></td>
                      <td className={`px-2 py-2.5 text-right font-mono text-xs ${isInc ? 'text-amber-500' : isDark ? 'text-[#10B981]' : 'text-[#059669]'}`}>{f.dil_100g != null ? f.dil_100g.toFixed(1) : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.dil_20g != null ? f.dil_20g.toFixed(1) : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.dil_30g != null ? f.dil_30g.toFixed(1) : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.dil_40g != null ? f.dil_40g.toFixed(1) : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.dil_50g != null ? f.dil_50g.toFixed(1) : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-xs text-gray-400">{f.dii_percent != null ? f.dii_percent : '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden lg:table-cell text-gray-400">{f.calories_100g ?? '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden lg:table-cell text-gray-400">{f.fiber_100g ?? '-'}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden lg:table-cell text-gray-400">{f.sat_fat_100g ?? '-'}</td>
                      <td className="px-2 py-2.5 hidden lg:table-cell">
                        {f.fodmap ? (<span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${f.fodmap === 'low' ? 'bg-green-100 text-green-700' : f.fodmap === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{f.fodmap === 'low' ? 'Low' : f.fodmap === 'medium' ? 'Med' : 'High'}</span>) : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isInc && (
                            <button onClick={() => autofillFood(f)} disabled={autofilling === f.id}
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}
                              title="Auto-fill от USDA" data-testid={`autofill-${f.id}`}>
                              {autofilling === f.id ? <Loader2 className="w-3.5 h-3.5 text-[#0EA5E9] animate-spin" /> : <Zap className="w-3.5 h-3.5 text-[#0EA5E9]" />}
                            </button>
                          )}
                          <button onClick={() => openEdit(f)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`} data-testid={`edit-food-${f.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-[#6366F1]" />
                          </button>
                          <button onClick={() => deleteFood(f.id)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`} data-testid={`delete-food-${f.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-[#EF4444]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={13} className="text-center py-8 text-gray-400 text-sm">Няма намерени храни</td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
          <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
            Показани: {filtered.length} / {total}
          </div>
        </CardContent>
      </Card>

      <FoodFormDialog open={showForm} onClose={() => setShowForm(false)} form={form} setForm={setForm} editFood={editFood} onSave={saveFood} isDark={isDark} />
      <UsdaSearchDialog open={showUsda} onClose={() => setShowUsda(false)} API={API} isDark={isDark} existingFoodNames={foods.map(f => f.food)} onAddFood={addFromUsda} />
    </div>
  );
}
